import { DEVICE_COMMAND_RESTART_TIMEOUT_MS } from "./waitForDeviceCommandOperatorResult";

export const DEVICE_INFO_REPORT_POLL_MS = 1000;

export type DeviceInfoReportWaitOutcome =
  | "successful"
  | "unsuccessful"
  | "cancelled";

/**
 * Bounded operator-status poll for a single report_device_info command.
 * Stops on terminal, timeout, or shouldStop (collapse / unmount).
 * Does not retry the command.
 */
export async function waitForDeviceInfoReport(args: {
  commandId?: string | null;
  getStatus: (
    commandId: string
  ) => Promise<{ operatorState?: string } | null>;
  shouldStop: () => boolean;
  now?: () => number;
  sleep?: (ms: number) => Promise<void>;
  pollMs?: number;
  timeoutMs?: number;
}): Promise<DeviceInfoReportWaitOutcome> {
  const commandId = args.commandId;
  if (!commandId) return "unsuccessful";
  if (args.shouldStop()) return "cancelled";

  const now = args.now || Date.now;
  const sleep =
    args.sleep || ((ms: number) => new Promise((resolve) => setTimeout(resolve, ms)));
  const pollMs = args.pollMs ?? DEVICE_INFO_REPORT_POLL_MS;
  const timeoutMs = args.timeoutMs ?? DEVICE_COMMAND_RESTART_TIMEOUT_MS;
  const deadline = now() + timeoutMs;

  while (now() <= deadline) {
    if (args.shouldStop()) return "cancelled";
    const status = await args.getStatus(commandId);
    if (args.shouldStop()) return "cancelled";
    const state = status?.operatorState;
    if (state === "successful") return "successful";
    if (state === "unsuccessful") return "unsuccessful";
    if (now() + pollMs > deadline) break;
    await sleep(pollMs);
  }
  if (args.shouldStop()) return "cancelled";
  return "unsuccessful";
}
